import { capture } from './analytics';

type RegoEvent =
  // Happy path
  | 'rego_started'
  | 'rego_auth_completed'
  | 'rego_checkout_initiated'
  | 'rego_payment_completed'
  | 'rego_completed'
  // Navigation & intent
  | 'rego_step_viewed'
  | 'rego_resume_from_existing'
  | 'rego_login_fallback'
  // Redirect flow
  | 'rego_redirect_resumed'
  | 'rego_redirect_failed'
  | 'rego_awaiting_settlement'
  | 'rego_settlement_resolved'
  | 'rego_settlement_timeout'
  | 'rego_settlement_keep_waiting'
  // Voucher (Multibanco)
  | 'rego_voucher_presented'
  // Hiccups
  | 'rego_payment_retry'
  | 'rego_session_recovery'
  // Email-first auth flow
  | 'rego_email_checked'
  // Shipping
  | 'rego_shipping_submitted'
  // Errors
  | 'rego_error';

interface RegoEventProperties {
  rego_started: {
    country_code?: string;
    is_returning?: boolean;
    attempt_number?: number;
    is_redirect_return?: boolean;
  };
  rego_auth_completed: { auth_mode: 'signup' | 'login' | 'complete-registration' };
  rego_checkout_initiated: {
    donation_amount: number;
    has_merch: boolean;
    currency?: string;
    checkout_mode?: 'hosted' | 'embedded';
  };
  rego_payment_completed: {
    result_code: string;
    donation_id?: string;
    payment_method?: string;
    amount?: number;
    currency?: string;
    is_redirect_return?: boolean;
    event_source?: 'checkout' | 'redirect_finalized' | 'webhook_settled' | 'paypal';
  };
  rego_completed: {
    donation_id?: string;
    is_waitlist: boolean;
    is_redirect_return?: boolean;
    skipped_donation?: boolean;
  };
  rego_step_viewed: {
    step: number;
    step_name: 'ride_selection' | 'auth' | 'preferences' | 'payment';
  };
  rego_resume_from_existing: {
    intent: 'resumePending' | 'changeRide' | 'auto';
    has_pending_registration: boolean;
    has_checkout_state: boolean;
  };
  rego_login_fallback: { from: 'forgot_password' | 'page_navigation' };
  rego_redirect_resumed: {
    donation_id?: string;
    resolution_kind: string;
    has_stored_session: boolean;
    webhook_settled?: boolean;
  };
  rego_redirect_failed: { donation_id?: string; reason: string };
  rego_awaiting_settlement: { donation_id?: string };
  rego_voucher_presented: { donation_id?: string; payment_method?: string };
  rego_settlement_resolved: {
    donation_id?: string;
    outcome:
      | 'completed'
      | 'payment_received_unauthenticated'
      | 'failed'
      | 'cancelled'
      | 'user-cancelled';
    poll_duration_ms?: number;
    poll_count?: number;
    settled_status?: string;
  };
  rego_settlement_timeout: { donation_id?: string; poll_duration_ms?: number; poll_count?: number };
  rego_settlement_keep_waiting: { donation_id?: string; poll_duration_ms?: number };
  rego_payment_retry: { previous_error?: string };
  rego_session_recovery: { outcome: 'succeeded' | 'failed'; donation_id?: string };
  rego_email_checked: {
    result: 'new' | 'existing' | 'existing-registered';
    is_retry: boolean;
    is_duplicate_switch?: boolean;
  };
  rego_shipping_submitted: { country: string; has_state: boolean };
  rego_error: {
    step: string;
    error_type: string;
    error_message: string;
    /** tRPC error code (e.g. NOT_FOUND, UNAUTHORIZED) when available. */
    error_code?: string;
    /** HTTP status from the failed request when available. */
    http_status?: number;
    /** Coarse bucket: network | timeout | server | auth | client | unknown. */
    kind?: string;
  };
}

export interface RegoContext {
  ride_id?: string;
  ride_name?: string;
  country_code?: string;
  current_step?: number;
  has_mandatory_donation?: boolean;
}

/**
 * Pull the diagnostic shape out of a (tRPC) error so save failures are
 * actionable in telemetry instead of a single hardcoded string. Returns props
 * ready to spread into a `rego_error` event.
 */
export function describeSaveError(err: unknown): {
  error_code?: string;
  http_status?: number;
  kind: string;
  error_message: string;
} {
  const e = err as { data?: { code?: string; httpStatus?: number }; message?: string } | undefined;
  const error_code = e?.data?.code;
  const http_status = e?.data?.httpStatus;
  const error_message = e?.message ?? 'Unknown error';
  let kind = 'unknown';
  if (http_status == null && error_code == null) kind = 'network';
  else if (http_status != null && http_status >= 500) kind = 'server';
  else if (
    http_status === 401 ||
    http_status === 403 ||
    error_code === 'UNAUTHORIZED' ||
    error_code === 'FORBIDDEN'
  )
    kind = 'auth';
  else if (http_status != null && http_status >= 400) kind = 'client';
  return { error_code, http_status, kind, error_message };
}

export function trackRego<E extends RegoEvent>(
  event: E,
  properties: RegoEventProperties[E],
  context?: Partial<RegoContext>,
): void {
  try {
    capture(event, {
      ...context,
      ...properties,
    });
  } catch {
    // Analytics must never break the registration flow
  }
}
